import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from 'styled-components';
import { RecoilRoot } from 'recoil';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { Amplify, Auth, Hub } from 'aws-amplify';
import reactGA from 'react-ga';
import config from '~/shared/config';
import theme from '~/shared/style/theme';
import Shell from './Shell';
import AuthScreen from './Auth/AuthScreen';
import '@aws-amplify/ui-react/styles.css';

const amplifyconfig: any = {
  Auth: {
    region: process.env.REACT_APP_COGNITO_REGION || 'us-east-1',
    userPoolId: process.env.REACT_APP_COGNITO_POOL_ID || 'us-east-1_sr2n4d09I',
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '13j6amf303a03dulhciq0vsahc',
    mandatorySignIn: false,
  },
};

Amplify.configure(amplifyconfig);

function AppScreen() {
  const queryClient = new QueryClient();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error('[GlobalError]', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // eslint-disable-next-line no-console
      console.error('[GlobalUnhandledRejection]', {
        reason: event.reason,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RecoilRoot>
              <Shell />
            </RecoilRoot>
          </Router>
        </ThemeProvider>
      </MuiThemeProvider>
    </QueryClientProvider>
  );
}

function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (config.ga?.userId) {
      reactGA.initialize(config.ga.userId);
    }

    Auth.currentAuthenticatedUser()
      .then(async user => {
        if (window.location.pathname.endsWith('complete-signup')) {
          try {
            await Auth.signOut();
          } catch (error) {}

          window.location.reload();
        }

        // This function creates anonymous visitor IDs in Pendo unless you change the visitor id field to use your app's values
        // This function uses the placeholder 'ACCOUNT-UNIQUE-ID' value for account ID unless you change the account id field to use your app's values
        // Call this function in your authentication promise handler or callback when your visitor and account id values are available
        // Please use Strings, Numbers, or Bools for value types.
        (window as unknown as any).pendo.initialize({
          visitor: {
            id: user.attributes.sub, // Required if user is logged in, default creates anonymous ID
            email: user.attributes.email, // Recommended if using Pendo Feedback, or NPS Email
            full_name: user.attributes.name, // Recommended if using Pendo Feedback
            role: user.authenticationFlowType, // Optional

            // You can add any additional visitor level key-values here,
            // as long as it's not one of the above reserved names.
          },

          account: {
            id:
              user.signInUserSession.accessToken.payload['cognito:groups']?.filter(
                (g: any) => !(g.includes('_BETA') || g.includes('_BASE') || g.includes('_DEMO')),
              )[0] || 'MKT',
            name: user.signInUserSession.accessToken.username,
            // is_paying:    // Recommended if using Pendo Feedback
            // monthly_value:// Recommended if using Pendo Feedback
            // planLevel:    // Optional
            // planPrice:    // Optional
            // creationDate: // Optional
            // You can add any additional account level key-values here,
            // as long as it's not one of the above reserved names.
          },
        });

        reactGA.set({
          userId: user.attributes.sub,
        });
        setReady(true);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(err);
      });

    Hub.listen('auth', async ({ payload: { event } }) => {
      if (event === 'signIn') {
        if (window.location.pathname.endsWith('complete-signup')) {
          window.location.href = '/';
        } else {
          window.location.reload();
        }
      }

      if (event === 'signOut') {
        setReady(false);
        window.location.reload();
      }
    });
  }, [setReady]);

  return <>{ready ? <AppScreen /> : <AuthScreen />}</>;
}

export default App;
